import { useCallback, useEffect, useRef, useState } from "react";
import type { BetbyInitInfo } from "@/betby/api/types";
import type {
  BTRendererInitializeConfig,
  BTRendererInstance,
} from "@/betby/types/betby";
import { useNavStore } from "@/betby/store/navStore";
import { useAuthStore } from "@/betby/store/authStore";
import { useNewOrderStore } from "@/betby/store/newOrderStore";
import {
  sendLoginMessage,
  sendNavigateMessage,
  sendRechargeMessage,
  sendTokenExpiredConfirmMessage,
  showNetworkRetryModal,
} from "@/betby/models/host/unityMessageService";
import { requestBalanceAndUpdateStore as requestBalanceAndUpdateStoreService } from "@/betby/models/balance/balanceService";
import { getFreshBetbyToken } from "@/betby/models/betby/betbyTokenService";
import LogUtil from "@/betby/utils/LogUtil";
import { TRACK_EVENT_TO_RECHARGE_SPM } from "@/betby/utils/TrackEvents";
import { useHideOrderListOnBonusPopup } from "@/betby/hooks/useHideOrderListOnBonusPopup";
import { useBetbyUrlChange } from "@/betby/hooks/useBetbyUrlChange";
import { useBetbyRedPointPolling } from "@/betby/hooks/useBetbyRedPointPolling";
import { useSportBalanceUpdateListener } from "@/betby/hooks/useSportBalanceUpdateListener";
import { useBetbyInitFlow } from "@/betby/hooks/useBetbyInitFlow";
import { registerBetbyKillHandler } from "@/betby/infrastructure/betby/betbyRuntime";
import { setBetbyInstance } from "@/betby/infrastructure/host/unityMessageRuntime";
import analyticsBridge from "@/betby/adapters/analytics/analyticsBridge";

const BETBY_CONTAINER_ID = "betby";
const LEGACY_BASENAME = "/sportsbook";

export const SETUP_THROTTLE_KEY = "betby-setup-throttle-time";

// 获取上次执行时间（从 localStorage）
export function getLastSetupTime(): number {
  const stored = localStorage.getItem(SETUP_THROTTLE_KEY);
  return stored ? parseInt(stored, 10) : 0;
}

// 设置上次执行时间（存入 localStorage）
export function setLastSetupTime(time: number): void {
  localStorage.setItem(SETUP_THROTTLE_KEY, String(time));
}

// 清空节流缓存，允许立即执行
export function clearSetupThrottle(): void {
  setLastSetupTime(0);
}

interface UseBetbyRendererViewModelOptions {
  basename?: string;
  currencyMode?: "gc" | "sc";
  hideTopBar?: boolean;
}

const isSameInitInfo = (
  prev: BetbyInitInfo | null,
  next: BetbyInitInfo | null,
): boolean => {
  if (!prev || !next) return false;
  return (
    prev.money_type === next.money_type &&
    prev.bet_by_token === next.bet_by_token &&
    prev.brand_id === next.brand_id &&
    prev.theme_name === next.theme_name &&
    prev.lang === next.lang
  );
};

const normalizeInitInfoList = (list: unknown[]): BetbyInitInfo[] => {
  if (!Array.isArray(list)) return [];
  return list.map((info) => {
    const typedInfo = info as BetbyInitInfo;
    return {
      ...typedInfo,
      money_type: String(typedInfo.money_type ?? "").toLowerCase() as
        | "gc"
        | "sc",
    };
  });
};

const pickInitInfoByMode = (
  list: BetbyInitInfo[],
  mode: string,
): BetbyInitInfo | null => {
  if (!list.length) return null;
  return list.find((info) => info.money_type.includes(mode)) ?? list[0] ?? null;
};

const resolveInitInfoFromResponse = (
  responseData: unknown,
  mode: string,
): BetbyInitInfo | null => {
  const response = responseData as { bet_by_init_info?: unknown[] } | null;
  const normalizedInitInfoList = normalizeInitInfoList(
    response?.bet_by_init_info ?? [],
  );
  return pickInitInfoByMode(normalizedInitInfoList, mode);
};

function resolveLegacyBasename(pathname?: string | null): string | undefined {
  if (!pathname) {
    return undefined;
  }

  return pathname.startsWith(LEGACY_BASENAME) ? LEGACY_BASENAME : undefined;
}

export function useBetbyRendererViewModel({
  basename,
  currencyMode,
  hideTopBar,
}: UseBetbyRendererViewModelOptions = {}) {
  const pathname =
    typeof window !== "undefined" ? window.location.pathname : null;
  const betbyBasename = basename ?? resolveLegacyBasename(pathname);
  const btInstance = useRef<BTRendererInstance | null>(null);
  const isInitializedRef = useRef(false);
  const lastInitInfoRef = useRef<BetbyInitInfo | null>(null);
  const skipKillInCleanupRef = useRef(false);
  const [initInfo, setInitInfo] = useState<BetbyInitInfo | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const {
    setOrderRedPointCount,
    updateBalance,
    forceLoginNeeded,
    hideOrderList,
    currentMode: currentModeFromStore,
    htb,
    isOrderListVisible,
  } = useNavStore();
  const { setNewOrderIds } = useNewOrderStore();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const accessToken = useAuthStore((state) => state.access_token);
  const effectiveCurrentMode = currencyMode ?? currentModeFromStore;
  const fixedHeaderHeight = (hideTopBar ?? htb === "1") ? 0 : 50;

  const showRetryModal = useCallback((onConfirm: () => void) => {
    showNetworkRetryModal({
      onConfirm,
      onClose: () => {
        // 启动期如果用户放弃重试，宿主侧按 token 过期流程统一处理退出。
        sendTokenExpiredConfirmMessage();
      },
    });
  }, []);

  const setInitInfoIfChanged = useCallback((nextInfo: BetbyInitInfo) => {
    setInitInfo((prev) => (isSameInitInfo(prev, nextInfo) ? prev : nextInfo));
  }, []);

  const requestBalanceAndUpdateStore = useCallback(
    (skipGlobalErrorHandler = true) =>
      requestBalanceAndUpdateStoreService(
        updateBalance,
        skipGlobalErrorHandler,
      ),
    [updateBalance],
  );

  const refreshBalance = useCallback(async () => {
    await requestBalanceAndUpdateStore(true);
  }, [requestBalanceAndUpdateStore]);

  const { startRedPointPolling, stopRedPointPolling } = useBetbyRedPointPolling(
    {
      currentMode: effectiveCurrentMode,
      isInitializedRef,
      refreshBalance,
      setOrderRedPointCount,
      setNewOrderIds,
    },
  );

  const doKill = useCallback(
    (skipActualKill = false) => {
      LogUtil.info("[BetbyRenderer] doKill 被调用", {
        skipActualKill,
        hasInstance: !!btInstance.current,
        isInitialized: isInitializedRef.current,
      });

      // skipActualKill: 货币切换时为 true，不主动调用 kill，让 SDK 在 initialize 时自动处理
      // 这样可以避免 kill 被调用两次（我们调用一次 + SDK 内部调用一次）
      if (btInstance.current) {
        if (skipActualKill) {
          // 清空引用，让 SDK 在新实例初始化时自动清理旧实例
          btInstance.current = null;
          LogUtil.info(
            "[BetbyRenderer] doKill: 跳过实际 kill，等待 SDK 自动清理",
          );
        } else {
          try {
            btInstance.current.kill();
            LogUtil.info("[BetbyRenderer] doKill: BT实例已销毁");
          } catch (error) {
            LogUtil.error("[BetbyRenderer] kill 异常:", error);
          }
          btInstance.current = null;
        }
      }

      isInitializedRef.current = false;
      stopRedPointPolling();
    },
    [stopRedPointPolling],
  );

  useEffect(() => {
    registerBetbyKillHandler(doKill);
    return () => {
      registerBetbyKillHandler(null);
    };
  }, [doKill]);

  const {
    isLoading,
    setIsLoading,
    initStartTimeRef,
    initPhaseBothSucceededRef,
    getIsPageStart,
    closeLoadingAndStartPolling,
    fetchInitInfo,
  } = useBetbyInitFlow({
    isAuthenticated,
    accessToken,
    forceLoginNeeded,
    isRendererInitializedRef: isInitializedRef,
    startRedPointPolling,
    setInitInfoIfChanged,
    requestBalanceAndUpdateStore,
    showRetryModal,
    resolveInitInfoFromResponse,
  });

  useEffect(() => {
    // 货币切换时跳过实际 kill，让 SDK 在 initialize 时自动处理旧实例
    doKill(true);
    initStartTimeRef.current = Date.now();

    const timeoutId = setTimeout(() => {
      if (getIsPageStart()) {
        setIsLoading(true);
      }
      void fetchInitInfo();
    }, 0);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [
    doKill,
    effectiveCurrentMode,
    fetchInitInfo,
    getIsPageStart,
    initStartTimeRef,
    refreshTrigger,
    setIsLoading,
  ]);

  const handleSessionRefresh = useCallback(() => {
    doKill();
    setRefreshTrigger((prev) => prev + 1);
  }, [doKill]);

  const postNavigateToParent = useCallback((url?: string) => {
    if (url) {
      sendNavigateMessage(url);
    }
  }, []);

  const buildRendererConfig =
    useCallback((): BTRendererInitializeConfig | null => {
      if (!initInfo) {
        LogUtil.warn("[BetbyRenderer] 初始化信息未准备好");
        return null;
      }

      return {
        brand_id: initInfo.brand_id,
        token: initInfo.bet_by_token,
        themeName: initInfo.theme_name,
        lang: initInfo.lang,
        target: document.getElementById(BETBY_CONTAINER_ID),
        betSlipOffsetTop: fixedHeaderHeight,
        stickyTop: fixedHeaderHeight,
        betslipZIndex: 999,
        ...(betbyBasename ? { basename: betbyBasename } : {}),
        onTokenExpired: () =>
          getFreshBetbyToken({
            currentMode: effectiveCurrentMode,
            setInitInfoIfChanged,
            markSkipCleanup: () => {
              // token 热更新只需要让下一轮初始化接管实例，避免 effect 清理阶段把新 token 流程一并杀掉。
              skipKillInCleanupRef.current = true;
            },
            resolveInitInfoFromResponse,
          }),
        onLogin: () => sendLoginMessage(),
        onRegister: () => sendLoginMessage(),
        onSessionRefresh: () => handleSessionRefresh(),
        onRecharge: () => {
          sendRechargeMessage();
          analyticsBridge.track(TRACK_EVENT_TO_RECHARGE_SPM, {
            src: 516,
            eb_key1: "2",
          });
        },
        onBetSlipStateChange: () => {
          void refreshBalance();
        },
        widgetParams: {
          onBannerClick: (params) => {
            postNavigateToParent(params.url);
          },
          onOutcomeClick: (params) => {
            postNavigateToParent(params.url);
          },
        },
      };
    }, [
      betbyBasename,
      effectiveCurrentMode,
      fixedHeaderHeight,
      handleSessionRefresh,
      initInfo,
      postNavigateToParent,
      refreshBalance,
      setInitInfoIfChanged,
    ]);

  const setupBTRenderer = useCallback((): boolean => {
    // 节流：1000ms 内仅执行一次（使用 localStorage 持久化）
    const now = Date.now();
    const lastSetupTime = getLastSetupTime();
    if (now - lastSetupTime < 10000) {
      LogUtil.info("[BetbyRenderer] setupBTRenderer 被节流，跳过执行");
      return false;
    }
    setLastSetupTime(now);
    if (typeof window === "undefined" || !window.BTRenderer) {
      LogUtil.warn("[BetbyRenderer] BTRenderer not available yet");
      return false;
    }

    const config = buildRendererConfig();
    if (!config) {
      return false;
    }

    try {
      btInstance.current = new window.BTRenderer();
      btInstance.current.initialize(config);
      isInitializedRef.current = true;
      setBetbyInstance(btInstance.current);
      LogUtil.info("[BetbyRenderer] BTRenderer initialized successfully", {
        brand_id: initInfo?.brand_id,
        theme: initInfo?.theme_name,
        lang: initInfo?.lang,
        money_type: initInfo?.money_type,
      });

      if (!initPhaseBothSucceededRef.current) {
        return true;
      }

      closeLoadingAndStartPolling();
      return true;
    } catch (error) {
      LogUtil.error("[BetbyRenderer] Initialization error:", error);
      if (!initPhaseBothSucceededRef.current) {
        return false;
      }

      closeLoadingAndStartPolling({ startPolling: false });
      return false;
    }
  }, [
    buildRendererConfig,
    closeLoadingAndStartPolling,
    initInfo,
    initPhaseBothSucceededRef,
  ]);

  useEffect(() => {
    if (!initInfo) {
      return;
    }

    // 只有当 initInfo.money_type 与当前模式匹配时才处理
    const modeMatches = initInfo.money_type.includes(effectiveCurrentMode);
    if (!modeMatches) {
      LogUtil.info(
        "[BetbyRenderer] initInfo useEffect: money_type 与当前模式不匹配，跳过",
        {
          money_type: initInfo.money_type,
          effectiveCurrentMode,
        },
      );
      return;
    }

    if (isInitializedRef.current && btInstance.current) {
      const prev = lastInitInfoRef.current;
      const onlyTokenChanged =
        prev &&
        prev.brand_id === initInfo.brand_id &&
        prev.theme_name === initInfo.theme_name &&
        prev.lang === initInfo.lang &&
        prev.money_type === initInfo.money_type &&
        prev.bet_by_token !== initInfo.bet_by_token;

      if (onlyTokenChanged) {
        // SDK 自身会在 token 续期后复用现有实例，这里只同步最新快照，避免无意义重建。
        lastInitInfoRef.current = initInfo;
        return;
      }

      try {
        LogUtil.info("[BetbyRenderer] initInfo useEffect body: BT实例已销毁");
        btInstance.current.kill();
      } catch (error) {
        LogUtil.error("[BetbyRenderer] 销毁旧实例失败:", error);
      }
      btInstance.current = null;
      isInitializedRef.current = false;

      setTimeout(() => {
        setIsLoading(true);
        initStartTimeRef.current = Date.now();
      }, 0);
    }

    if (isInitializedRef.current) {
      return;
    }

    lastInitInfoRef.current = initInfo;

    if (!btInstance.current && initStartTimeRef.current === 0) {
      initStartTimeRef.current = Date.now();
    }

    let initRetryTimer: ReturnType<typeof setTimeout> | null = null;
    let disposed = false;

    const checkAndInit = () => {
      if (disposed) {
        return;
      }
      if (setupBTRenderer()) {
        return;
      }
      initRetryTimer = setTimeout(checkAndInit, 100);
    };

    const handleScriptLoaded = () => {
      checkAndInit();
    };

    if (typeof window !== "undefined") {
      if (window.BTRenderer) {
        checkAndInit();
      } else {
        window.addEventListener("bt-renderer-loaded", handleScriptLoaded);
        checkAndInit();
      }
    }

    return () => {
      disposed = true;
      if (initRetryTimer) {
        clearTimeout(initRetryTimer);
      }
      if (typeof window !== "undefined") {
        window.removeEventListener("bt-renderer-loaded", handleScriptLoaded);
      }

      if (skipKillInCleanupRef.current) {
        skipKillInCleanupRef.current = false;
        return;
      }

      if (btInstance.current) {
        // 这里只处理真正的 effect 卸载/重建；token 热更新场景前面已通过 skipKill 跳过。
        try {
          btInstance.current.kill();
        } catch (error) {
          LogUtil.error("[BetbyRenderer] Error killing BTRenderer:", error);
        }
        btInstance.current = null;
        isInitializedRef.current = false;
      }
      stopRedPointPolling();
    };
  }, [
    effectiveCurrentMode,
    initInfo,
    initStartTimeRef,
    setIsLoading,
    setupBTRenderer,
    stopRedPointPolling,
  ]);

  useSportBalanceUpdateListener({ refreshBalance });

  useBetbyUrlChange({
    initInfo,
    isInitializedRef,
    pathname,
    basename: betbyBasename,
  });

  useHideOrderListOnBonusPopup({
    enabled: isOrderListVisible,
    hideOrderList,
    clearNewOrders: () => useNewOrderStore.getState().clearAll(),
    getIsOrderListVisible: () => useNavStore.getState().isOrderListVisible,
    containerId: BETBY_CONTAINER_ID,
  });

  return {
    containerId: BETBY_CONTAINER_ID,
    isLoading,
  };
}
